import React, { useState } from "react";
import { withRouter } from "react-router-dom";
import toast from "react-hot-toast";
import DOMPurify from "dompurify";
import style from "./Activity.module.scss";
import { AddCommentModal } from "../../../tickets/components/add-comment-modal";
import { dateFormatter } from "../../../../../components/helper/helper";
import useTicketStore from '../../../../stores/ticket';
import useDomainStore from '../../../../stores/domain';
import EmptyClipboardIcon from "../../../../assets/svg/icons/common/EmptyClipboardIcon";
import TicketStatusIcon from "../../../tickets/components/TicketStatusIcon";
import {
  getTicketStatusIconType,
  getTicketStatusSlug,
} from "../../../tickets/ticketStatusUtils";
import CopyIcon16 from "../../../../assets/svg/icons/common/CopyIcon16";
import ChevronLeft16 from "../../../../assets/svg/icons/common/ChevronLeft16";
import ChevronRight16 from "../../../../assets/svg/icons/common/ChevronRight16";

function OpenTickets({ activeApp, history, tickets, currentTicketIndex, onPrevTicket, onNextTicket, isMobile = false }) {
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [commentTicketId, setCommentTicketId] = useState(null);
  const { setSelectedTicket } = useTicketStore((state) => ({
    setSelectedTicket: state.setSelectedTicket
  }));
  // Server-authoritative: getApplicationsTickets() reports it per ticket, since
  // the assignee rule differs row to row. Deciding it client-side from the role
  // alone hid the control from org admins and assignees who are allowed to use
  // it, and showed it to callers addComment() would 403.
  const commentTicket = tickets?.find((t) => t?.id === commentTicketId);
  const canUpdateStatus = !!commentTicket?.can_update_status;
  const isKurvDomain = useDomainStore((state) => state.isKurvDomain);
  const handleCopyTicket = async (ticketId) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(ticketId);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = ticketId;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      toast.success("Ticket number copied to clipboard");
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error("Failed to copy ticket number");
    }
  };

  const handleGoToTickets = () => {
    if (!activeApp?.id) {
      return;
    }
    const base = history.location.pathname.includes("/v2/")
      ? "/v2"
      : "";
    history.push({
      pathname: `${base}/service-tickets/merchant/${activeApp.id}`,
      state: { merchantDba: activeApp?.field_dba || "" },
    });
  };

  if (!tickets || tickets.length === 0) {
    return (
      <div className={`${style.card} ${style.tickets_card} ${isKurvDomain ? style.kurv_theme : ""}`}>
        <div className={style.ticket_header}>
          <div className={style.title_section}>
            <h6>Open Tickets</h6>
          </div>
        </div>
        <div className={`${style.ticket_details} ${style.empty_state}`}>
          <div className={style.empty_message}>
            <EmptyClipboardIcon />
            <p>No open tickets found</p>
          </div>
          <button
            type="button"
            className={style.go_to_tickets}
            onClick={handleGoToTickets}
          >
            Go to tickets
          </button>
        </div>
      </div>
    );
  }

  const currentStatus = tickets[currentTicketIndex]?.status;
  const statusSlug = getTicketStatusSlug(currentStatus);

  return (
    <div className={`${style.card} ${style.tickets_card} ${isKurvDomain ? style.kurv_theme : ""}`}>
      <div className={style.ticket_header}>
        <div className={style.title_section}>
          <h6>Open Tickets</h6>
          <div
            className={`${style.status_badge} ${style[`ticket_status_${statusSlug}`]}`}
          >
            <TicketStatusIcon
              type={getTicketStatusIconType(currentStatus)}
              className={style.ticket_status_svg}
            />
            <span>{currentStatus}</span>
          </div>
        </div>
        <button
          type="button"
          className={style.add_comment_button}
          onClick={() => {
            const t = tickets[currentTicketIndex];
            setSelectedTicket(t);
            setCommentTicketId(t?.id ?? null);
            setIsCommentModalOpen(true);
          }}
        >
          Add comment
        </button>
      </div>

      <div className={style.ticket_details}>
        <div className={style.details_row}>
          <div className={style.details_col}>
            <span className={style.label}>Ticket #</span>
            <div className={style.value}>
              <span>{tickets[currentTicketIndex]?.id}</span>
              <button
                type="button"
                className={style.copy_button}
                onClick={() => handleCopyTicket(tickets[currentTicketIndex]?.id)}
              >
                <CopyIcon16 />
              </button>
            </div>
          </div>
          <div className={style.details_col}>
            <span className={style.label}>Problem Type</span>
            <span className={style.value}>{tickets[currentTicketIndex]?.problem_type}</span>
          </div>
          <div className={style.details_col}>
            <span className={style.label}>Last comment</span>
            <span className={style.value}>{dateFormatter(tickets[currentTicketIndex]?.last_comment)}</span>
          </div>
        </div>
      </div>
      <div className={style.ticket_text}>
        <p
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(tickets[currentTicketIndex]?.comment || ""),
          }}
        />
      </div>
      <div className={style.ticket_footer}>
        <div className={style.ticket_pagination}>
          <div className={style.pagination_buttons}>
            <button
              type="button"
              className={style.prev_button}
              disabled={currentTicketIndex === 0}
              onClick={onPrevTicket}
            >
              <ChevronLeft16 color={currentTicketIndex === 0 ? "#BFBFC3" : "#3A383F"} />
            </button>
            <button
              type="button"
              className={style.next_button}
              disabled={currentTicketIndex === tickets.length - 1}
              onClick={onNextTicket}
            >
              <ChevronRight16 color={currentTicketIndex === tickets.length - 1 ? "#BFBFC3" : "#3A383F"} />
            </button>
          </div>
          <span>Ticket {currentTicketIndex + 1} / {tickets.length}</span>
        </div>
        <button
          type="button"
          className={style.go_to_tickets}
          onClick={handleGoToTickets}
        >
          Go to tickets
        </button>
      </div>
      <AddCommentModal
        isOpen={isCommentModalOpen}
        onClose={() => {
          setIsCommentModalOpen(false);
          setCommentTicketId(null);
        }}
        ticketId={commentTicketId}
        canUpdateStatus={canUpdateStatus}
      />
    </div>
  );
}

export default withRouter(OpenTickets);